import { useEffect, useRef } from 'react';
import { API_ENDPOINTS } from '../config';
import { getToken } from '../utils/auth';

export type WhatsAppListActivityEvent = {
  type: 'LEAD_ACTIVITY_UPDATED' | 'WHATSAPP_REPLY_RECEIVED';
  lead_id: string;
  activity_at: string;
  direction: 'inbound' | 'outbound';
  phone_identity?: string;
  name?: string;
  message_preview?: string;
  message_at?: string;
  unread_count?: number;
  needs_reply?: boolean;
  first_unanswered_at?: string | null;
  assigned_to?: string | null;
};

type WhatsAppTeamStateEvent = {
  type: 'WHATSAPP_TEAM_STATE_CHANGED';
  activity_at: string;
};

const RETRY_DELAYS = [1000, 2000, 5000, 10000, 30000];

export function useWhatsAppListWebSocket(
  active: boolean,
  view: 'queue' | 'unassigned' | 'team',
  onEvent: (event: WhatsAppListActivityEvent) => void,
  onReconnect?: () => void,
  onTeamEvent?: (event: WhatsAppTeamStateEvent) => void,
) {
  const eventRef = useRef(onEvent);
  eventRef.current = onEvent;
  const teamEventRef = useRef(onTeamEvent);
  teamEventRef.current = onTeamEvent;

  useEffect(() => {
    if (!active) return;
    const token = getToken();
    if (!token) return;

    let socket: WebSocket | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    let attempts = 0;

    const connect = () => {
      if (stopped) return;
      socket = new WebSocket(API_ENDPOINTS.WS.WHATSAPP_LIST(view, token));

      socket.onopen = () => {
        attempts = 0;
        onReconnect?.();
      };

      socket.onmessage = (message) => {
        try {
          const event = JSON.parse(String(message.data)) as WhatsAppListActivityEvent | WhatsAppTeamStateEvent;
          if (event.type === 'WHATSAPP_TEAM_STATE_CHANGED' && event.activity_at) {
            teamEventRef.current?.(event);
          } else if ((event.type === 'LEAD_ACTIVITY_UPDATED' || event.type === 'WHATSAPP_REPLY_RECEIVED') && event.lead_id && event.activity_at) {
            eventRef.current(event);
          }
        } catch {
          // Ignore malformed realtime frames and let the normal refresh recover state.
        }
      };

      socket.onerror = () => socket?.close();
      socket.onclose = () => {
        if (stopped) return;
        const delay = RETRY_DELAYS[Math.min(attempts++, RETRY_DELAYS.length - 1)];
        retryTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      stopped = true;
      if (retryTimer) clearTimeout(retryTimer);
      socket?.close(1000, 'component cleanup');
    };
  }, [active, view]);
}
